// src/components/Navbar.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../store/authSlice";
import { useSocket } from "../socket/SocketContext";
import { hasUserPermission, normalizeRole, ROLES } from "../utils/rbacHelper";
import { clearAuthScopedClientState } from "../utils/authSession";
import { navigateToAdminDashboard, readDashboardTabFromSearch } from "../router/dashboardNavigation";
import { isDemoReadOnlyUser } from "../utils/demoUser";

import notificationSound from "../assets/noti.wav";
import { SOUND_MAP } from "../assets/soundMap";
import { isAppSoundEnabled } from "../utils/appSound";

// ✅ MUI Icons
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PaidIcon from "@mui/icons-material/Paid";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RoomIcon from "@mui/icons-material/Room";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import GpsOffIcon from "@mui/icons-material/GpsOff";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import SpeedIcon from "@mui/icons-material/Speed";
import ShieldIcon from "@mui/icons-material/Shield";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import MapIcon from "@mui/icons-material/Map";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BarChartIcon from "@mui/icons-material/BarChart";
import RouteIcon from "@mui/icons-material/Route";
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import KeyIcon from "@mui/icons-material/Key";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import DeviceModelModal from "../components/modals/device_model_modal";
import logoImage from "../assets/logo.png";
import { useWhiteLabelRuntime } from "../modules/whitelabel/runtime/WhiteLabelRuntimeProvider";



// ✅ MUI
import {
  AppBar,
  Toolbar,
  Box,
  Stack,
  IconButton,
  Typography,
  Button,
  Badge,
  Chip,
  Avatar,
  Menu as MuiMenu,
  MenuItem,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Popover,
  useMediaQuery,
  TextField,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useQuickModal } from "../hooks/QuickModalContext";
import { useChangeMyPasswordMutation } from "../redux/admin/userApi";
import { AppDialog, AppDrawer } from "../components/popup";

// Alarm Config (same)
const ALARM_CONFIG = {
  sos_alarm: { icon: CrisisAlertIcon, color: "#dc2626", label: "SOS Emergency" },
  panic_button: { icon: ShieldIcon, color: "#dc2626", label: "Panic Alert" },
  speeding: { icon: SpeedIcon, color: "#ef4444", label: "Speeding" },
  device_offline: { icon: WifiOffIcon, color: "#6b7280", label: "Device Offline" },
  gps_signal_lost: { icon: GpsOffIcon, color: "#eab308", label: "GPS Lost" },
  low_battery: { icon: BatteryAlertIcon, color: "#f97316", label: "Low Battery" },
  geofence_entry: { icon: RoomIcon, color: "#3b82f6", label: "Geofence Entry" },
  geofence_exit: { icon: RoomIcon, color: "#8b5cf6", label: "Geofence Exit" },
  tampering: { icon: ShieldIcon, color: "#dc2626", label: "Tampering" },
  fuel_theft: { icon: LocalGasStationIcon, color: "#dc2626", label: "Fuel Theft" },
  idle_too_long: { icon: AccessTimeIcon, color: "#eab308", label: "Idle Alert" },
};


// Small helper for MUI Tabs
function a11yProps(index) {
  return { id: `notif-tab-${index}`, "aria-controls": `notif-tabpanel-${index}` };
}

function normalizeMessageNotification(item = {}) {
  return {
    _id: item._id || item.notificationId || item.broadcastId || `${item.senderId || "msg"}-${item.createdAt || Date.now()}`,
    title: item.title || (item.senderName ? `Message from ${item.senderName}` : "New Message"),
    message: item.message || "",
    read: Boolean(item.read),
    type: item.type || "broadcast",
    createdAt: item.createdAt || new Date().toISOString(),
  };
}

function normalizeSystemNotification(item = {}) {
  return {
    _id: item._id || item.notificationId || `system-${item.createdAt || Date.now()}`,
    title: item.title || "Notification",
    message: item.message || item.body || "",
    read: Boolean(item.read),
    type: item.eventType || item.type || "info",
    createdAt: item.createdAt || new Date().toISOString(),
    source: "system",
  };
}

function extractAlarmCoordinates(alarm = {}) {
  const lat = Number(alarm?.location?.latitude ?? alarm?.location?.lat ?? alarm?.latitude ?? alarm?.lat);
  const lng = Number(alarm?.location?.longitude ?? alarm?.location?.lng ?? alarm?.longitude ?? alarm?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

function getAttributeValue(attributes = [], key) {
  const normalizedKey = String(key || "").trim().toLowerCase();
  const found = Array.isArray(attributes)
    ? attributes.find((item) => String(item?.key || item?.label || "").trim().toLowerCase() === normalizedKey)
    : null;
  return found?.value;
}

export default function Navbar({ activeTab, setActiveTab }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const runtimeBrand = useWhiteLabelRuntime();
  const currentUser = useSelector((state) => state.auth.user);
  const [changeMyPassword, { isLoading: isChangingPassword }] = useChangeMyPasswordMutation();
  const {
    socket,
    on,
  } = useSocket();
  const currentUserRole = normalizeRole(currentUser?.role);

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // UI State
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const [username, setUsername] = useState("");
  const [walletBalance, setWalletBalance] = useState(null);

  // Dropdowns
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profileOpen = Boolean(profileAnchorEl);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordSuccess, setChangePasswordSuccess] = useState("");

  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const notifOpen = Boolean(notifAnchorEl);
  // Notifications State (same behavior)
  const [notifications, setNotifications] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alarmCount, setAlarmCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [alarmAddressMap, setAlarmAddressMap] = useState({});
  const [alarmAddressStatus, setAlarmAddressStatus] = useState({});
  const [deviceModelOpen, setDeviceModelOpen] = useState(false);
  const geocodeMetaRef = useRef({});


  // Tabs: all | alarms | messages
  const [activeNotifTab, setActiveNotifTab] = useState("all");
  const tabIndex = activeNotifTab === "all" ? 0 : activeNotifTab === "alarms" ? 1 : 2;
  const isImpersonating = Boolean(localStorage.getItem("adminFallbackToken"));

  // Audio refs
  const audioRef = useRef(new Audio(notificationSound));
 const { openModal } = useQuickModal();

  // ✅ same: quick action
  const fireQuickAction = useCallback((type) => {
    // navigate("/admin-dashboard", { state: { quickAction: type } });
    openModal(type);
  }, [openModal]);




// ✅ Role-based UI visibility helpers (Navbar.jsx)

const isViewer = currentUserRole === ROLES.VIEWER;
const isDemoReadOnly = isDemoReadOnlyUser(currentUser);

// ✅ Client + Viewer => "saara gayab"
const isNoUiRole = useMemo(() => {
  return [ROLES.VIEWER, ROLES.CLIENT].includes(currentUserRole);
}, [currentUserRole]);

const canShowTasksMenu = useMemo(() => {
  if (isDemoReadOnly) return false;
  // tasks only for: admin, manager, office_emp
  return [ROLES.SUPER_ADMIN, ROLES.MANAGER, ROLES.OFFICE_EMPLOYEE].includes(currentUserRole);
}, [currentUserRole, isDemoReadOnly]);

const canShowAddDealerIcon = useMemo(() => {
  // hide for: dealer, user, client, viewer
  if (!currentUserRole) return false;
  if ([ROLES.DEALER, ROLES.USER, ROLES.CLIENT, ROLES.VIEWER].includes(currentUserRole)) return false;
  return hasUserPermission(currentUser, "createUser");
}, [currentUser, currentUserRole]);

const canShowAddUserIcon = useMemo(() => {
  if (!currentUserRole) return false;
  if (![ROLES.SUPER_ADMIN, ROLES.MANAGER, ROLES.SUB_ADMIN, ROLES.DEALER].includes(currentUserRole)) return false;
  return hasUserPermission(currentUser, "createUser");
}, [currentUser, currentUserRole]);

const canShowAddViewerIcon = useMemo(() => {
  if (!currentUserRole) return false;
  return currentUserRole === ROLES.USER;
}, [currentUserRole]);

const canShowCenterQuickBar = useMemo(() => {
  // ✅ client + viewer + field employee => no top quick bar
  if (!currentUserRole) return false;
  if (currentUserRole === ROLES.FIELD_EMPLOYEE) return false;
  return currentUser && !isNoUiRole && !isMdDown;
}, [currentUser, isNoUiRole, isMdDown, currentUserRole]);


const canShowAddSubAdminIcon = useMemo(() => {
  if (!currentUserRole) return false;

  // ❌ hide for: sub_admin, dealer, user, client, viewer
  if ([ROLES.SUB_ADMIN, ROLES.DEALER, ROLES.USER, ROLES.CLIENT, ROLES.VIEWER].includes(currentUserRole)) return false;

  // ✅ only those who can create users
  return hasUserPermission(currentUser, "createUser");
}, [currentUser, currentUserRole]);

const canShowCameraNavbarIcon = useMemo(() => {
  if (currentUserRole === ROLES.SUPER_ADMIN) return true;
  if ([ROLES.USER, ROLES.DEALER, ROLES.SUB_ADMIN].includes(currentUserRole)) {
    return Boolean(getAttributeValue(currentUser?.attributes, "video"));
  }
  return false;
}, [currentUserRole, currentUser]);

const canShowReportsQuickAction = useMemo(() => {
  return currentUser?.permissions?.other?.disableReports !== true;
}, [currentUser?.permissions]);

const canShowInventoryIcon = useMemo(() => {
  return ![ROLES.USER, ROLES.SUB_ADMIN, ROLES.DEALER, ROLES.FIELD_EMPLOYEE].includes(currentUserRole);
}, [currentUserRole]);

const canShowTokenIcon = useMemo(() => {
  return ![ROLES.SUPER_ADMIN, ROLES.CLIENT, ROLES.VIEWER, ROLES.USER, ROLES.FIELD_EMPLOYEE].includes(currentUserRole);
}, [currentUserRole]);

const canShowPurchaseLicenseQuickAction = useMemo(() => {
  return hasUserPermission(currentUser, "purchaseLicense");
}, [currentUser]);

const canShowTopMessageShortcut = useMemo(() => {
  if (isDemoReadOnly) return false;
  return [ROLES.SUPER_ADMIN, ROLES.MANAGER].includes(currentUserRole);
}, [currentUserRole, isDemoReadOnly]);


// ✅ Menu items logic (ONLY MUI ICONS) + DROPDOWN ITEMS (role controlled)
const getMenuItems = useCallback(() => {
  const items = [];
  const logisticsRoles = new Set([
    ROLES.SUPER_ADMIN,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.MANAGER,
    ROLES.HR,
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.USER,
    ROLES.CLIENT,
  ]);
  if (!currentUserRole) return items;

  // ✅ CLIENT + VIEWER + FIELD EMPLOYEE => no navbar module icons
  if ([ROLES.CLIENT, ROLES.VIEWER, ROLES.FIELD_EMPLOYEE].includes(currentUserRole)) return items;

  // ✅ Dashboard route page
  if (hasUserPermission(currentUser, "viewDashboard")) {
    items.push({
      id: "dashboard",
      icon: BarChartIcon,
      label: "Dashboard",
      isRoute: true,
      path: "/dashboard",
    });
  }

  // ✅ Live map
  if (hasUserPermission(currentUser, "viewMap")) {
    items.push({ id: "map", icon: TravelExploreIcon, label: "Live Map" });
  }

  // ✅ Tasks route (only admin/manager/office_emp)
  if (canShowTasksMenu) {
    items.push({
      id: "task_management",
      icon: AssignmentTurnedInIcon,
      label: "Tasks",
      isRoute: true,
      path: "/task-management",
    });
  }

  // ✅ My work route
  if (currentUserRole === ROLES.FIELD_EMPLOYEE) {
    items.push({
      id: "my_tasks",
      icon: EventAvailableIcon,
      label: "My Work",
      isRoute: true,
      path: "/my-tasks",
    });
  }

  if (logisticsRoles.has(currentUserRole)) {
    items.push({
      id: "logistics",
      icon: LocalShippingIcon,
      label: "Logistics",
      isRoute: true,
      path: "/details",
    });
  }

  if (canShowCameraNavbarIcon) {
    items.push({
      id: "camera_portal",
      icon: PhotoCameraIcon,
      label: "Camera",
      isRoute: true,
      path: "/camera-portal",
    });
  }

  if (canShowInventoryIcon) {
    items.push({
      id: "inventory",
      icon: AssignmentTurnedInIcon,
      label: "Inventory",
      isRoute: true,
      path: "/inventory",
    });
  }

  items.push({
    id: "groups",
    icon: DashboardCustomizeIcon,
    label: "Groups",
    isRoute: true,
    path: "/groups",
  });

  return items;
}, [
  canShowCameraNavbarIcon,
  canShowInventoryIcon,
  canShowTasksMenu,
  currentUser,
  currentUserRole,
]);





const menuItems = useMemo(() => getMenuItems(), [
  currentUserRole,
  canShowTasksMenu,
  canShowCameraNavbarIcon,
]);


// ✅ Role change hote hi instant UI refresh / stuck fix
useEffect(() => {
  // close any open popovers/menus so UI doesn't feel "stuck"
  setProfileAnchorEl(null);
  setNotifAnchorEl(null);

  // optional: mobile drawer bhi close
  setMobileOpen(false);
  }, [currentUserRole, activeTab]);

  const playNavbarSound = useCallback((soundKey = "default") => {
    if (!isAppSoundEnabled()) return;
    const soundFile = SOUND_MAP[String(soundKey || "default").trim().toLowerCase()] || notificationSound;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = soundFile;
        audioRef.current.load();
        audioRef.current.play().catch(() => {});
      }
    } catch (_) {}
  }, []);

  // Audio init (same intent)
  useEffect(() => {
    audioRef.current.load();

    const unlock = () => {
      audioRef.current
        .play()
        .then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        })
        .catch(() => {});
       
      window.removeEventListener("click", unlock);
    };
    window.addEventListener("click", unlock);
  }, []);

  // Fetch data (same endpoints/logic)
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { "x-auth-token": token } };

        const profRes = await axios.get(`${backendUrl}/auth/profile`, config);
        setUsername(profRes.data.username);

        if (canShowTokenIcon) {
          const profileId = String(profRes?.data?._id || currentUser?._id || "");
          const licensesRes = await axios.get(`${backendUrl}/logistics/licenses`, config);
          const licenses = Array.isArray(licensesRes.data) ? licensesRes.data : [];
          let remainingTokens = licenses.reduce((sum, item) => {
            const assignedTo = String(item?.assignedTo?._id || item?.assignedTo || "");
            const isActiveLicense = !item?.isExpired && String(item?.status || "").toLowerCase() !== "expired";
            if (!profileId || assignedTo !== profileId || !isActiveLicense) return sum;
            return sum + Number(item?.remainingCount || 0);
          }, 0);

          if (remainingTokens <= 0) {
            try {
              const walletRes = await axios.get(`${backendUrl}/credits/my-wallet`, config);
              remainingTokens = Math.max(0, Number(walletRes?.data?.balance || 0));
            } catch {
              remainingTokens = 0;
            }
          }

          setWalletBalance(remainingTokens);
        } else {
          setWalletBalance(null);
        }

        const [notifRes, systemNotifRes, alarmRes] = await Promise.all([
          axios.get(`${backendUrl}/messaging/inbox?filter=all`, config),
          axios.get(`${backendUrl}/notificationswebapp/user?limit=20`, config).catch(() => ({ data: { notifications: [] } })),
          axios.get(`${backendUrl}/alarms?status=active`, config),
        ]);
        const inbox = Array.isArray(notifRes.data?.data) ? notifRes.data.data : [];
        const systemInbox = Array.isArray(systemNotifRes.data?.notifications)
          ? systemNotifRes.data.notifications
          : [];
        const normalizedInbox = [...inbox.map(normalizeMessageNotification), ...systemInbox.map(normalizeSystemNotification)]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 10);
        setNotifications(normalizedInbox);
        if (alarmRes.data.alarms) {
          setAlarms(alarmRes.data.alarms.slice(0, 10));
          setAlarmCount(alarmRes.data.alarms.length);
        }

        setUnreadCount(normalizedInbox.filter((item) => !item.read).length);
      } catch (err) {
        console.error("Error fetching navbar data:", err);
      }
    };
    fetchData();
  }, [currentUser, backendUrl, canShowTokenIcon]);

  // Socket listeners (same)
  useEffect(() => {
    if (!socket) return;

    const getAlarmId = (alarm) =>
      alarm?._id || alarm?.alarmId || alarm?.id || null;

    const normalizeAlarm = (data) => {
      const alarm = data?.alarm || data;
      if (!alarm || !alarm.alarmType) return null;
      const normalizedId = getAlarmId(alarm);
      if (!normalizedId) return null;
      return {
        ...alarm,
        _id: normalizedId,
      };
    };

    socket.on("broadcast_message", (msg) => {
      const normalizedMessage = normalizeMessageNotification(msg);
      playNavbarSound("default");
      triggerAnimation();
      setNotifications((prev) => {
        const alreadyExists = prev.some(
          (item) => String(item._id) === String(normalizedMessage._id)
        );
        if (!alreadyExists && !normalizedMessage.read) {
          setUnreadCount((count) => count + 1);
        }
        const merged = [normalizedMessage, ...prev.filter((item) => String(item._id) !== String(normalizedMessage._id))];
        return merged.slice(0, 10);
      });
    });

    socket.on("notification", (msg) => {
      const normalizedMessage = normalizeSystemNotification(msg);
      playNavbarSound("default");
      triggerAnimation();
      setNotifications((prev) => {
        const alreadyExists = prev.some((item) => String(item._id) === String(normalizedMessage._id));
        if (!alreadyExists && !normalizedMessage.read) {
          setUnreadCount((count) => count + 1);
        }
        const merged = [normalizedMessage, ...prev.filter((item) => String(item._id) !== String(normalizedMessage._id))];
        return merged.slice(0, 10);
      });
    });

    const offAlarmEvent = on("alarm_event", (data) => {
      const alarm = normalizeAlarm(data);
      if (!alarm || !alarm.alarmType) return;
      if (
        alarm.eventType === "alarm_acknowledged" ||
        alarm.eventType === "alarm_resolved" ||
        alarm.eventType === "alarm_status_update"
      ) {
        return;
      }
      if (alarm.status === "resolved") return;

      playNavbarSound(alarm.sound || "default");
      triggerAnimation();

      setAlarms((prev) => {
        if (prev.find((a) => a._id === alarm._id)) return prev;
        return [alarm, ...prev].slice(0, 10);
      });
    });

    const offAlarmStatus = on("alarm_status_update", (data) => {
      const alarmId = data?.alarmId || data?._id;
      if (!alarmId) return;
      const status = data?.status;
      if (status === "resolved") {
        setAlarms((prev) => prev.filter((a) => String(a._id) !== String(alarmId)));
        setAlarmCount((prev) => Math.max(0, prev - 1));
        return;
      }
      if (status === "acknowledged") {
        setAlarms((prev) =>
          prev.map((a) =>
            String(a._id) === String(alarmId) ? { ...a, status: "acknowledged" } : a
          )
        );
      }
    });

    socket.on("wallet_updated", (newBalance) => {
      setWalletBalance(newBalance);
    });

    return () => {
      socket.off("broadcast_message");
      socket.off("notification");
      socket.off("wallet_updated");
      offAlarmEvent?.();
      offAlarmStatus?.();
    };
  }, [socket, on, playNavbarSound]);

  useEffect(() => {
    const activeAlarmTotal = alarms.filter((alarm) => alarm?.status !== "resolved").length;
    setAlarmCount(activeAlarmTotal);
  }, [alarms]);

  const triggerAnimation = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 700);
  };

  // logout (same)
  const handleLogout = () => {
    const adminFallbackToken = localStorage.getItem("adminFallbackToken");
    if (adminFallbackToken) {
      clearAuthScopedClientState();
      localStorage.setItem("token", adminFallbackToken);
      localStorage.removeItem("adminFallbackToken");
      localStorage.removeItem("user");
      window.location.href = "/dashboard";
    } else {
      dispatch(logout());
      navigate("/login");
      setMobileOpen(false);
      setProfileAnchorEl(null);
    }
  };

  // helpers (same)
  const formatTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

const getAlarmIcon = (type) => {
  const config = ALARM_CONFIG[type];
  const IconComponent = config?.icon || WarningAmberIcon;
  const color = config?.color || "#6b7280";
  return <IconComponent sx={{ fontSize: 18, color }} />;
};


  const getAlarmLabel = (type) => ALARM_CONFIG[type]?.label || type.replace(/_/g, " ");
  const getAlarmColor = (type) => ALARM_CONFIG[type]?.color || "#6b7280";

  const openAlarmMap = useCallback((alarm) => {
    const coords = extractAlarmCoordinates(alarm);
    if (!coords) return;
    window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, "_blank", "noopener,noreferrer");
  }, []);

  const getSeverityChipSx = (severity) => {
    const map = {
      critical: { bg: alpha(theme.palette.error.main, 0.12), fg: theme.palette.error.main },
      high: { bg: alpha(theme.palette.error.main, 0.1), fg: theme.palette.error.main },
      medium: { bg: alpha(theme.palette.warning.main, 0.14), fg: theme.palette.warning.main },
      low: { bg: alpha(theme.palette.info.main, 0.12), fg: theme.palette.info.main },
    };
    const val = map[severity] || { bg: alpha(theme.palette.text.primary, 0.06), fg: theme.palette.text.primary };
    return { bgcolor: val.bg, color: val.fg, fontWeight: 700, textTransform: "capitalize" };
  };

  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const totalCount = unreadCount + alarmCount;


const handleNavItemClick = (id, item) => {
  // 1) Route menu item
  if (item?.isRoute && item?.path) {
    navigate(item.path);
    setMobileOpen(false);
    return;
  }

  // 2) Tab-based item → go admin-dashboard then set tab
  navigateToAdminDashboard(navigate, { tab: id });

  if (setActiveTab) setActiveTab(id);

  setMobileOpen(false);
};

  const resolvedActiveTab =
    location.pathname === "/dashboard"
      ? readDashboardTabFromSearch(location.search, "assignments")
      : activeTab;


  // MUI: open/close handlers
  const toggleMobile = () => setMobileOpen((v) => !v);

  const handleOpenProfile = (e) => setProfileAnchorEl(e.currentTarget);
  const handleCloseProfile = () => setProfileAnchorEl(null);
  const handleOpenChangePassword = () => {
    setChangePasswordError("");
    setChangePasswordSuccess("");
    setChangePasswordForm({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setChangePasswordOpen(true);
    handleCloseProfile();
  };
  const handleCloseChangePassword = () => {
    if (isChangingPassword) return;
    setChangePasswordOpen(false);
  };
  const handleChangePasswordField = (field) => (event) => {
    setChangePasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
  };
  const handleSubmitChangePassword = async () => {
    const { oldPassword, newPassword, confirmPassword } = changePasswordForm;
    setChangePasswordError("");
    setChangePasswordSuccess("");

    if (!oldPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setChangePasswordError("All password fields are required.");
      return;
    }

    if (newPassword.trim().length < 6) {
      setChangePasswordError("New password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangePasswordError("New password and confirm password do not match.");
      return;
    }

    try {
      const response = await changeMyPassword({
        oldPassword: oldPassword.trim(),
        newPassword: newPassword.trim(),
        confirmPassword: confirmPassword.trim(),
      }).unwrap();

      setChangePasswordSuccess(response?.msg || "Password changed successfully.");
      setChangePasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        setChangePasswordOpen(false);
      }, 900);
    } catch (error) {
      setChangePasswordError(
        error?.data?.msg ||
          error?.data?.message ||
          "Failed to change password."
      );
    }
  };

  const handleOpenNotif = (e) => {
    setNotifAnchorEl(e.currentTarget);
  };
  const handleCloseNotif = () => setNotifAnchorEl(null);

  // Notif tab handler
  const handleNotifTab = (_, newIndex) => {
    const map = ["all", "alarms", "messages"];
    setActiveNotifTab(map[newIndex] || "all");
  };

  const brandBlue = "#1d63c8";
  const brandBlueHover = "#114fae";
  const brandBlueTint = "rgba(29, 99, 200, 0.1)";
  const brandBlueTintStrong = "rgba(29, 99, 200, 0.16)";
  const brandBlueBorder = "rgba(29, 99, 200, 0.28)";
  const tooltipSx = {
    "& .MuiTooltip-tooltip": {
      background: `linear-gradient(135deg, ${brandBlue} 0%, ${brandBlueHover} 100%)`,
      color: "#fff",
      border: "1px solid rgba(120, 207, 255, 0.28)",
      boxShadow: "0 10px 24px rgba(29, 99, 200, 0.22)",
      fontWeight: 700,
    },
    "& .MuiTooltip-arrow": {
      color: brandBlueHover,
    },
  };

  // ---- UI styles (no external CSS) ----
  const appBarSx = {
    bgcolor: "background.paper",
    color: "text.primary",
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    zIndex: (t) => t.zIndex.drawer + 10, // ✅ IMPORTANT (Dashboard overlays se upar)

  };

  const iconBtnSx = (active = false) => ({
    width: "clamp(40px, 2.4vw, 52px)",
    height: "clamp(40px, 2.4vw, 52px)",
    borderRadius: 2,
    border: `1px solid ${active ? brandBlueBorder : alpha("#334155", 0.16)}`,
    bgcolor: active ? brandBlueTintStrong : alpha("#f8fafc", 0.96),
    color: active ? brandBlue : "#475569",
    boxShadow: active
      ? `0 10px 22px ${alpha(brandBlue, 0.18)}`
      : `0 6px 14px ${alpha("#0f172a", 0.06)}`,
    transition: "all 0.18s ease",
    "&:hover": {
      bgcolor: active ? "rgba(29, 99, 200, 0.2)" : brandBlueTint,
      borderColor: active ? "rgba(29, 99, 200, 0.4)" : brandBlueBorder,
      color: brandBlue,
      transform: "translateY(-1px)",
    },
  ...(isAnimating
      ? {
          animation: "muiShake 0.7s ease-in-out",
          "@keyframes muiShake": {
            "0%, 100%": { transform: "translateX(0)" },
            "20%": { transform: "translateX(-2px)" },
            "40%": { transform: "translateX(2px)" },
            "60%": { transform: "translateX(-2px)" },
            "80%": { transform: "translateX(2px)" },
          },
        }
      : {}),
  });

  const quickActionBtnSx = {
    width: "clamp(40px, 2.4vw, 52px)",
    height: "clamp(40px, 2.4vw, 52px)",
    borderRadius: 2,
    border: `1px solid ${alpha("#cbd5e1", 0.9)}`,
    bgcolor: alpha("#f8fafc", 0.96),
    color: "#475569",
    boxShadow: `0 6px 14px ${alpha("#0f172a", 0.06)}`,
    transition: "all 0.18s ease",
    "&:hover": {
      bgcolor: brandBlueTint,
      borderColor: brandBlueBorder,
      color: brandBlue,
      transform: "translateY(-1px)",
    },
  };

  const notifPaperSx = {
    mt: 1,
    width: isSmDown ? "92vw" : 360,
    minWidth: isSmDown ? "min(92vw, 320px)" : 340,
    maxWidth: isSmDown ? "92vw" : 380,
    borderRadius: 3,
    overflow: "hidden",
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    boxShadow: `0 14px 40px ${alpha(theme.palette.common.black, 0.12)}`,
    ...(isSmDown
      ? {}
      : {
          left: "auto !important",
          right: "8px !important",
        }),
  };

  const rowSx = {
    px: 1.5,
    py: 1.25,
    display: "flex",
    gap: 1.25,
    alignItems: "flex-start",
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
    "&:last-of-type": { borderBottom: "none" },
    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
  };

  const dotPulseSx = {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    width: 10,
    height: 10,
    borderRadius: 999,
    bgcolor: theme.palette.error.main,
    boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.5)}`,
    animation: "pulse 1.4s infinite",
    "@keyframes pulse": {
      "0%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.45)}` },
      "70%": { boxShadow: `0 0 0 10px ${alpha(theme.palette.error.main, 0)}` },
      "100%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}` },
    },
  };

  // Drawer content (mobile)
  const drawer = (
    <Box sx={{ width: 320, maxWidth: "92vw" }} role="presentation">
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Typography sx={{ fontWeight: 800 }}>Menu</Typography>
        <IconButton onClick={toggleMobile} sx={iconBtnSx(false)}>
         <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {currentUser && (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              mb: 2,
            }}
          >
            <Avatar sx={{ width: 46, height: 46, fontWeight: 800 }}>
              {getUserInitials(username || currentUser.username)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800 }} noWrap>
                {username || currentUser.username}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {currentUser.role}
              </Typography>
            </Box>
          </Paper>
        )}

        <List sx={{ p: 0 }}>
          {currentUser ? (
            <>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive =
                  item?.isRoute && item?.path
                    ? location.pathname === item.path
                    : resolvedActiveTab === item.id;
                return (
                  <ListItemButton
                    key={item.id}
                    onClick={() => handleNavItemClick(item.id, { isRoute: item.isRoute, path: item.path })}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 38 }}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                );
              })}

              <ListItemButton component={RouterLink} to="/profile" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2, mb: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 38 }}>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItemButton>

              {canShowTokenIcon && walletBalance !== null && (
                <Box sx={{ mt: 1, mb: 2 }}>
                 <Chip
  icon={<PaidIcon fontSize="small" />}
  label={`Remaining Tokens: ${walletBalance}`}
  variant="outlined"
  sx={{ borderRadius: 2, fontWeight: 700 }}
/>

                </Box>
              )}

            {currentUser && !isViewer && (
  <Box sx={{ mt: 1 }}>
    <Typography sx={{ fontWeight: 800, mb: 1 }} variant="body2" color="text.secondary">
      Quick Actions
    </Typography>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
      }}
    >
      <Button variant="outlined" onClick={() => fireQuickAction("devices")} sx={{ borderRadius: 2 }}>
        Devices List
      </Button>

      <Button variant="outlined" onClick={() => fireQuickAction("route")} sx={{ borderRadius: 2 }}>
        Add Route
      </Button>

      {canShowPurchaseLicenseQuickAction && (
        <Button variant="outlined" onClick={() => fireQuickAction("license")} sx={{ borderRadius: 2 }}>
          Purchase License
        </Button>
      )}

      <Button variant="outlined" onClick={() => fireQuickAction("expense")} sx={{ borderRadius: 2 }}>
        Add Expense
      </Button>

      <Button variant="outlined" onClick={() => fireQuickAction("driver")} sx={{ borderRadius: 2 }}>
        Add Driver
      </Button>

      <Button variant="outlined" onClick={() => fireQuickAction("sensor")} sx={{ borderRadius: 2 }}>
        Add Sensor
      </Button>

      <Button variant="outlined" onClick={() => fireQuickAction("reminder")} sx={{ borderRadius: 2 }}>
        Add Reminder
      </Button>

      {canShowReportsQuickAction && (
        <Button variant="outlined" onClick={() => fireQuickAction("report")} sx={{ borderRadius: 2 }}>
          Route Report
        </Button>
      )}

      {canShowAddSubAdminIcon && (
  <Button
    variant="outlined"
    onClick={() => {
      fireQuickAction("subAdmin");
      setMobileOpen(false);
    }}
    sx={{ borderRadius: 2 }}
  >
    Add Admin
  </Button>
)}


      {/* ✅ Mobile Add Dealer/User buttons */}
      {canShowAddDealerIcon && (
        <Button
          variant="outlined"
          onClick={() => {
            fireQuickAction("dealer");
            setMobileOpen(false);
          }}
          sx={{ borderRadius: 2 }}
        >
          Add Dealer
        </Button>
      )}

      {canShowAddUserIcon && (
        <Button
          variant="outlined"
          onClick={() => {
            fireQuickAction("user");
            setMobileOpen(false);
          }}
          sx={{ borderRadius: 2 }}
        >
          Create User
        </Button>
      )}

      {canShowTopMessageShortcut && (
        <Button
          variant="outlined"
          onClick={() => {
            navigate("/message");
            setMobileOpen(false);
          }}
          sx={{ borderRadius: 2 }}
        >
          Messages
        </Button>
      )}

      {canShowAddViewerIcon && (
        <Button
          variant="outlined"
          onClick={() => {
            fireQuickAction("viewer");
            setMobileOpen(false);
          }}
          sx={{ borderRadius: 2 }}
        >
          Add Viewer
        </Button>
      )}
    </Box>
  </Box>
)}


              <Divider sx={{ my: 2 }} />
              <Button
                onClick={handleLogout}
                fullWidth
                variant="contained"
                color="error"
                startIcon={<LogoutIcon fontSize="small" />}
                sx={{ borderRadius: 2, py: 1.1, fontWeight: 800 }}
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                component={RouterLink}
                to="/login"
                onClick={() => setMobileOpen(false)}
                fullWidth
                variant="outlined"
startIcon={<LoginIcon fontSize="small" />}
                sx={{ borderRadius: 2, py: 1.1, fontWeight: 800, mb: 1 }}
              >
                Login
              </Button>
              <Button
                component={RouterLink}
                to="/signup"
                onClick={() => setMobileOpen(false)}
                fullWidth
                variant="contained"
                startIcon={<PersonAddAltIcon fontSize="small" />}
                sx={{ borderRadius: 2, py: 1.1, fontWeight: 800 }}
              >
                Signup
              </Button>
              
            </>
          )}
        </List>
      </Box>
    </Box>
  );

  // Visible lists in notification popover
  const visibleAlarms = alarms;
  const visibleMessages = notifications;

  const desktopNavItems = useMemo(() => {
    const deduped = [];
    const seen = new Set();

    menuItems.forEach((item) => {
      const key = item?.id || item?.path;
      if (!key || seen.has(key)) return;
      seen.add(key);
      deduped.push(item);
    });

    return deduped;
  }, [menuItems]);

  const desktopQuickActions = useMemo(() => {
    const items = [
      { id: "devices", label: "Devices List", icon: DirectionsCarFilledIcon, onClick: () => fireQuickAction("devices") },
      { id: "reminder", label: "Add Reminder", icon: EventAvailableIcon, onClick: () => fireQuickAction("reminder") },
      { id: "subAdmin", label: "Add Admin", icon: AdminPanelSettingsIcon, onClick: () => fireQuickAction("subAdmin"), visible: canShowAddSubAdminIcon },
      { id: "dealer", label: "Add Dealer", icon: PersonAddAltIcon, onClick: () => fireQuickAction("dealer"), visible: canShowAddDealerIcon },
      { id: "viewer", label: "Add Viewer", icon: VisibilityOutlinedIcon, onClick: () => fireQuickAction("viewer"), visible: canShowAddViewerIcon },
      { id: "route", label: "Add Route", icon: MapIcon, onClick: () => fireQuickAction("route") },
      { id: "license", label: "Purchase License", icon: ShieldIcon, onClick: () => fireQuickAction("license"), visible: canShowPurchaseLicenseQuickAction },
      { id: "expense", label: "Add Expense", icon: PaidIcon, onClick: () => fireQuickAction("expense") },
      { id: "driver", label: "Add Driver", icon: AdminPanelSettingsIcon, onClick: () => fireQuickAction("driver") },
      { id: "sensor", label: "Add Sensor", icon: WarningAmberIcon, onClick: () => fireQuickAction("sensor") },
      { id: "report", label: "Route Report", icon: RouteIcon, onClick: () => fireQuickAction("report"), visible: canShowReportsQuickAction },
      { id: "messages", label: "Messages", icon: ChatBubbleOutlineIcon, onClick: () => navigate("/message"), visible: canShowTopMessageShortcut },
      { id: "user", label: "Create User", icon: SupervisedUserCircleIcon, onClick: () => fireQuickAction("user"), visible: canShowAddUserIcon },
      {
        id: "modelMaster",
        label: "Model Master",
        icon: DeveloperBoardIcon,
        onClick: () => setDeviceModelOpen(true),
        visible: currentUserRole !== ROLES.USER,
      },
    ];

    return items.filter((item) => item.visible !== false);
  }, [
    canShowAddDealerIcon,
    canShowAddSubAdminIcon,
    canShowAddUserIcon,
    canShowAddViewerIcon,
    canShowPurchaseLicenseQuickAction,
    canShowReportsQuickAction,
    canShowTopMessageShortcut,
    currentUserRole,
    fireQuickAction,
    navigate,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchAlarmAddresses = async () => {
      if (!notifOpen || !visibleAlarms.length) return;
      const token = localStorage.getItem("token");
      if (!token) return;

      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://app.krush.in/api";
      const candidates = visibleAlarms
        .map((alarm) => {
          const alarmId = String(alarm?._id || "");
          const coords = extractAlarmCoordinates(alarm);
          const existingAddress = alarm?.location?.address || alarmAddressMap[alarmId];
          const meta = geocodeMetaRef.current[alarmId];
          const sameCoords =
            meta &&
            coords &&
            Math.abs(Number(meta.lat) - coords.lat) < 0.0002 &&
            Math.abs(Number(meta.lng) - coords.lng) < 0.0002;
          const stillFresh = meta?.at ? Date.now() - meta.at < 45000 : false;
          if (!alarmId || !alarm?.deviceId || !coords || existingAddress || (sameCoords && stillFresh)) return null;
          return { alarmId, deviceId: alarm?.deviceId, lat: coords.lat, lng: coords.lng };
        })
        .filter(Boolean);

      if (!candidates.length) return;

      setAlarmAddressStatus((prev) => ({
        ...prev,
        ...Object.fromEntries(candidates.map(({ alarmId }) => [alarmId, "loading"])),
      }));

      for (const { alarmId, deviceId, lat, lng } of candidates) {
        if (cancelled) return;
        let status = "failed";
        let resolved = "";
        try {
          const response = await axios.get(
            `${baseUrl}/devices/${deviceId}/address?lat=${lat}&lng=${lng}`,
            { headers: { "x-auth-token": token } }
          );
          resolved =
            response?.data?.address?.full ||
            response?.data?.address?.display_name ||
            response?.data?.address?.address ||
            response?.data?.address ||
            "";
          status = resolved ? "success" : "failed";
        } catch (_) {
          status = "failed";
        }

        geocodeMetaRef.current[alarmId] = { lat, lng, at: Date.now() };
        if (!cancelled) {
          setAlarmAddressStatus((prev) => ({ ...prev, [alarmId]: status }));
          if (resolved) {
            setAlarmAddressMap((prev) => ({ ...prev, [alarmId]: resolved }));
          }
        }
      }
    };

    fetchAlarmAddresses();
    return () => {
      cancelled = true;
    };
  }, [notifOpen, visibleAlarms, alarmAddressMap]);

  return (
    <AppBar position="sticky" elevation={0} sx={appBarSx}>
      <Toolbar sx={{ gap: 1.5, minHeight: { xs: 64, md: 72 } }}>
        {/* LEFT: Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              textDecoration: "none",
              color: "inherit",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                bgcolor: "#ffffff",
                border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
                boxShadow: `0 10px 22px ${alpha(theme.palette.common.black, 0.12)}`,
              }}
            >
              <Box
                component="img"
                src={runtimeBrand.logoUrl || logoImage}
                alt={runtimeBrand.brandTitle || "GPS Controller"}
                sx={{ width: 34, height: 34, objectFit: "contain" }}
              />
            </Box>

            <Box sx={{ display: { xs: "none", sm: "block" }, whiteSpace: "nowrap" }}>
              <Typography sx={{ fontWeight: 900, letterSpacing: 0.4, lineHeight: 1.05 }}>
                {runtimeBrand.brandTitle || "GPS Controller"}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600, lineHeight: 1.1 }}>
                {runtimeBrand.brandSubtitle || "Track. Monitor. Protect."}
              </Typography>
            </Box>
          </Box>
        </Box>

{/* CENTER: Desktop Quick Actions (ROLE CONTROLLED) */}
{canShowCenterQuickBar && (
  <Box sx={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
    <Paper
      variant="outlined"
      sx={{
        px: 1,
        py: 0.8,
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        gap: 0.8,
        maxWidth: "100%",
        overflowX: "auto",
        scrollbarWidth: "thin",
        bgcolor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: "blur(10px)",
      }}
    >
      {desktopNavItems.map((item) => {
        const Icon = item.icon || DashboardCustomizeIcon;
        const isActive =
          item?.isRoute && item?.path
            ? location.pathname === item.path
            : resolvedActiveTab === item.id;
        return (
          <Tooltip key={item.id} title={item.label} arrow slotProps={{ popper: { sx: tooltipSx } }}>
            <IconButton
              onClick={() => handleNavItemClick(item.id, { isRoute: item.isRoute, path: item.path })}
              sx={iconBtnSx(isActive)}
              size="small"
            >
              <Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      })}

      {desktopQuickActions.map((item) => {
        const Icon = item.icon;
        return (
          <Tooltip key={item.id} title={item.label} arrow slotProps={{ popper: { sx: tooltipSx } }}>
            <IconButton onClick={item.onClick} sx={quickActionBtnSx} size="small">
              <Icon fontSize="small" sx={{ fontSize: 22, color: "inherit" }} />
            </IconButton>
          </Tooltip>
        );
      })}
    </Paper>
  </Box>
)}


        {/* RIGHT */}
        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          {currentUser && (
            <>
              {!canShowCenterQuickBar && canShowTopMessageShortcut && (
                <Tooltip title="Messages" arrow slotProps={{ popper: { sx: tooltipSx } }}>
                  <IconButton
                    onClick={() => navigate("/message")}
                    sx={quickActionBtnSx}
                    size="small"
                  >
                    <ChatBubbleOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}

              {!canShowCenterQuickBar && canShowAddUserIcon && (
                <Tooltip title="Create User" arrow slotProps={{ popper: { sx: tooltipSx } }}>
                  <IconButton
                    onClick={() => fireQuickAction("user")}
                    sx={quickActionBtnSx}
                    size="small"
                  >
                    <SupervisedUserCircleIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Remaining Tokens */}
              {canShowTokenIcon && walletBalance !== null && (
                <Chip
icon={<PaidIcon fontSize="small" />}
                  label={walletBalance}
                  title="Remaining Tokens"
                  variant="outlined"
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    borderRadius: 999,
                    fontWeight: 900,
                    px: 0.5,
                  }}
                />
              )}


              {/* Notifications */}
              <Tooltip title="Notifications" arrow slotProps={{ popper: { sx: tooltipSx } }}>
                <IconButton
                  onClick={handleOpenNotif}
                  sx={{
                    ...iconBtnSx(false),
                    borderColor: alarmCount > 0 ? alpha(theme.palette.error.main, 0.4) : alpha(theme.palette.divider, 0.9),
                  }}
                >
                  <Badge
                    badgeContent={totalCount > 99 ? "99+" : totalCount}
                    color={alarmCount > 0 ? "error" : "primary"}
                    overlap="circular"
                    invisible={totalCount <= 0}
                  >
                  <NotificationsNoneIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Popover
                open={notifOpen}
                anchorEl={notifAnchorEl}
                onClose={handleCloseNotif}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: notifPaperSx }}
                 sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
              >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  <Typography sx={{ fontWeight: 900 }}>Notifications</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Tabs
                      value={tabIndex}
                      onChange={handleNotifTab}
                      variant="fullWidth"
                      sx={{
                        minHeight: 38,
                        "& .MuiTab-root": { minHeight: 38, fontWeight: 900, textTransform: "none" },
                      }}
                    >
                      <Tab label="All" {...a11yProps(0)} />
                      <Tab
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span>Alarms</span>
                            {alarmCount > 0 && (
                              <Chip size="small" label={alarmCount} sx={{ height: 20, fontWeight: 900 }} />
                            )}
                          </Box>
                        }
                        {...a11yProps(1)}
                      />
                      <Tab label="Messages" {...a11yProps(2)} />
                    </Tabs>
                  </Box>
                </Box>

                {/* List */}
                <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                  {/* Alarms */}
                  {(activeNotifTab === "all" || activeNotifTab === "alarms") &&
                    visibleAlarms.map((alarm) => (
                      <Box
                        key={alarm._id}
                        sx={{ position: "relative", cursor: extractAlarmCoordinates(alarm) ? "pointer" : "default" }}
                        onClick={() => openAlarmMap(alarm)}
                      >
                        <Box sx={rowSx}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              bgcolor: alpha(getAlarmColor(alarm.alarmType), 0.12),
                              border: `1px solid ${alpha(getAlarmColor(alarm.alarmType), 0.18)}`,
                              flex: "0 0 auto",
                              mt: 0.2,
                            }}
                          >
                            {getAlarmIcon(alarm.alarmType)}
                          </Box>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                              <Typography sx={{ fontWeight: 900 }} noWrap>
                                {getAlarmLabel(alarm.alarmType)}
                              </Typography>
                              <Chip
                                size="small"
                                label={alarm.severity}
                                sx={{ height: 22, ...getSeverityChipSx(alarm.severity) }}
                              />
                            </Box>

                            <Typography variant="body2" sx={{ mt: 0.3 }} noWrap>
                              {alarm.deviceName || "Unknown Device"}
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.45,
                                whiteSpace: "normal",
                                lineHeight: 1.35,
                              }}
                            >
                              {alarm.message ||
                                `${alarm.deviceName || "Device"} reported ${getAlarmLabel(alarm.alarmType)}.`}
                            </Typography>

                            {extractAlarmCoordinates(alarm) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5, whiteSpace: "normal", lineHeight: 1.4 }}
                              >
                                <RoomIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }} />
                                {alarm.location?.address ||
                                  alarmAddressMap[String(alarm._id)] ||
                                  (alarmAddressStatus[String(alarm._id)] === "loading"
                                    ? "Loading address..."
                                    : "Tap to open location on map")}
                              </Typography>
                            )}

                            <Stack direction="row" spacing={0.7} sx={{ mt: 0.75, flexWrap: "wrap" }} useFlexGap>
                              <Chip
                                size="small"
                                label={formatTime(alarm.triggeredAt)}
                                variant="outlined"
                                sx={{ height: 22, fontWeight: 700 }}
                              />
                              {Number.isFinite(Number(alarm?.data?.speed)) && (
                                <Chip
                                  size="small"
                                  label={`Speed ${Number(alarm.data.speed)} km/h`}
                                  variant="outlined"
                                  sx={{ height: 22, fontWeight: 700 }}
                                />
                              )}
                              {alarm?.data?.vehicleStatus && (
                                <Chip
                                  size="small"
                                  label={String(alarm.data.vehicleStatus)}
                                  variant="outlined"
                                  sx={{ height: 22, fontWeight: 700, textTransform: "capitalize" }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Box>

                        {alarm.status === "active" && <Box sx={dotPulseSx} />}
                      </Box>
                    ))}

                  {/* Messages */}
                  {(activeNotifTab === "all" || activeNotifTab === "messages") &&
                    visibleMessages.map((item) => (
                      <Box
                        key={item._id}
                        sx={{
                          ...rowSx,
                          bgcolor: item.read ? "transparent" : alpha(theme.palette.info.main, 0.04),
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                            flex: "0 0 auto",
                            mt: 0.2,
                          }}
                        >
                         <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4 }}>
                            {formatTime(item.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}

                  {/* Empty */}
                  {((activeNotifTab === "alarms" && visibleAlarms.length === 0) ||
                    (activeNotifTab === "messages" && visibleMessages.length === 0) ||
                    (activeNotifTab === "all" && visibleAlarms.length === 0 && visibleMessages.length === 0)) && (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Box sx={{ display: "grid", placeItems: "center", mb: 1 }}>
                        <NotificationsNoneIcon sx={{ fontSize: 34 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 900 }}>No notifications</Typography>
                      <Typography variant="body2" color="text.secondary">
                        You’re all caught up.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                 <Button
  size="small"
  onClick={() => {
    handleCloseNotif();
    navigate("/notifications",); // Alert Logs tab
    // setActiveLabel("events");
    // setActiveTab?.("events");
  }}
  sx={{ fontWeight: 900, textTransform: "none" }}
>
  View All Alarms
</Button>


                  <Typography variant="caption" color="text.secondary">
                    {totalCount > 0 ? `${totalCount} total` : ""}
                  </Typography>
                </Box>
              </Popover>

              {/* Profile */}
              {!isSmDown && isImpersonating && (
                <Tooltip title="Exit User Login" arrow slotProps={{ popper: { sx: tooltipSx } }}>
                  <IconButton
                    onClick={handleLogout}
                    sx={iconBtnSx(false)}
                    size="small"
                  >
                    <ExitToAppIcon />
                  </IconButton>
                </Tooltip>
              )}

              {!isSmDown && (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 999,
                    px: 1,
                    py: 0.6,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                  }}
                  onClick={handleOpenProfile}
                >
                  <Avatar sx={{ width: 34, height: 34, fontWeight: 900 }}>
                    {getUserInitials(username || currentUser.username)}
                  </Avatar>
                  <Box sx={{ lineHeight: 1.05, pr: 0.6 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                      Hello,
                    </Typography>
                    <Typography sx={{ fontWeight: 900, maxWidth: 160 }} noWrap>
                      {username || currentUser.username}
                    </Typography>
                  </Box>
                </Paper>
              )}

              <MuiMenu
                anchorEl={profileAnchorEl}
                open={profileOpen}
                onClose={handleCloseProfile}
                PaperProps={{
                  sx: {
                    mt: 1,
                    width: 220,
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    boxShadow: `0 14px 40px ${alpha(theme.palette.common.black, 0.12)}`,
                  },
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
              >
                <MenuItem onClick={handleOpenChangePassword} sx={{ py: 1.2, gap: 1 }}>
                  <KeyIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 900 }}>Change Password</Typography>
                </MenuItem>
                <Divider />
                <MenuItem component={RouterLink} to="/profile" onClick={handleCloseProfile} sx={{ py: 1.2, gap: 1 }}>
                 <AccountCircleIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 900 }}>Profile</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ py: 1.2, gap: 1, color: theme.palette.error.main }}>
                <LogoutIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 900 }}>Logout</Typography>
                </MenuItem>
              </MuiMenu>

              <AppDialog
                open={changePasswordOpen}
                onClose={handleCloseChangePassword}
                maxWidth="sm"
                title="Change Password"
                actions={<><Button onClick={handleCloseChangePassword} disabled={isChangingPassword}>Cancel</Button><Button variant="contained" onClick={handleSubmitChangePassword} disabled={isChangingPassword}>{isChangingPassword ? "Saving..." : "Update Password"}</Button></>}
              >
                  <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
                    {changePasswordError ? (
                      <Alert severity="error">{changePasswordError}</Alert>
                    ) : null}
                    {changePasswordSuccess ? (
                      <Alert severity="success">{changePasswordSuccess}</Alert>
                    ) : null}
                    <TextField
                      label="Old Password"
                      type="password"
                      value={changePasswordForm.oldPassword}
                      onChange={handleChangePasswordField("oldPassword")}
                      autoComplete="current-password"
                      fullWidth
                    />
                    <TextField
                      label="New Password"
                      type="password"
                      value={changePasswordForm.newPassword}
                      onChange={handleChangePasswordField("newPassword")}
                      autoComplete="new-password"
                      fullWidth
                    />
                    <TextField
                      label="Confirm Password"
                      type="password"
                      value={changePasswordForm.confirmPassword}
                      onChange={handleChangePasswordField("confirmPassword")}
                      autoComplete="new-password"
                      fullWidth
                    />
                  </Box>
              </AppDialog>
            </>
          )}

          {/* Mobile hamburger */}
          {isMdDown && (
            <IconButton onClick={toggleMobile} sx={iconBtnSx(mobileOpen)} size="small">
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
        </Box>
      </Toolbar>
      <DeviceModelModal
  open={deviceModelOpen}
  onClose={() => setDeviceModelOpen(false)}
/>

      {/* Mobile Drawer */}
      <AppDrawer
        anchor="right"
        open={mobileOpen}
        onClose={toggleMobile}
        keepMounted
        closeButton={false}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          },
        }}
      >
        {drawer}
      </AppDrawer>
    </AppBar>

  );
}
